// Global
import React from 'react';

// Local
import { ProductResponse } from 'lib/services/models/content-response';
import { useSiteSettings } from 'lib/hooks/useSiteSettings';
import { getActionToolbarButtons } from 'components/helpers/ActionToolbar/Utils/ActionToolbar.utils';
import { Base, Data } from '@generated/Foundation.GMI.model';
import { ProductContentContainer } from './ProductContentContainer/ProductContentContainer';
import { ProductHero } from './ProductHero/ProductHero';
import { extractProductHeroData } from './ProductHero/ProductHero.utils';
import { SiteSettings } from 'lib/page-props-factory/plugins/site-settings';
import { ProcessedBreadcrumbItem } from 'lib/page-props-factory/plugins/breadcrumb';
import { useGlobalData } from 'lib/hooks/sitecore/useGlobalData';
import { AllergenClaimItem } from './ProductAllergenStatement/ProductAllergenStatement';
import { LinkFieldValue } from '@sitecore-content-sdk/nextjs';

export interface ProductDetailProps {
  product: ProductResponse;
  siteSettings?: SiteSettings | null;
  productRouteDetails?: Base.BasePage;
  breadcrumbData?: ProcessedBreadcrumbItem[] | null;
}

function toNonNegativeInt(value: unknown, fallback: number = 0): number {
  const num =
    typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : Number.NaN;

  if (!Number.isFinite(num)) return fallback;
  return Math.max(0, Math.trunc(num));
}

function unwrapValue<T>(item: T | { value?: T | null } | null | undefined): T | null {
  if (!item) return null;
  if (typeof item === 'object' && 'value' in item) {
    return ((item as { value?: T | null }).value ?? null) as T | null;
  }
  return item as T;
}

function toCtaField(label?: string, link?: string): LinkFieldValue | undefined {
  const text = label?.trim();
  const href = link?.trim();

  if (!text || !href) return undefined;

  return { href, text };
}

export function ProductDetail(props: ProductDetailProps) {
  const { product, productRouteDetails, breadcrumbData } = props;
  const { siteName } = useGlobalData();
  const isGMFSSite = siteName === 'GMFS';

  const siteSettings = useSiteSettings();

  const productDetailSettings =
    siteSettings?.productDetailSettings as Data.Settings.ProductDetailSettings;

  const heroActionToolbarSettings = getActionToolbarButtons(
    productDetailSettings?.fields?.productHeroActionToolBar as Data.Content.ActionToolBarButton[]
  );

  const contentActionToolbarSettings = getActionToolbarButtons(
    productDetailSettings?.fields?.contentActionToolBarButtons as Data.Content.ActionToolBarButton[]
  );

  const heroActionToolbarIdentifier =
    productDetailSettings?.fields?.productHeroEventPosition?.value;

  const contentActionToolbarIdentifier = productDetailSettings?.fields?.contentEventPosition?.value;

  const primaryCta = toCtaField(
    productDetailSettings?.fields?.primaryCtaLabel?.value,
    productDetailSettings?.fields?.primaryCtaLink?.value
  );

  const secondaryCta = toCtaField(
    productDetailSettings?.fields?.secondaryCtaLabel?.value,
    productDetailSettings?.fields?.secondaryCtaLink?.value
  );

  // This is For NAF
  const allergenClaims: AllergenClaimItem[] | null =
    productDetailSettings?.fields?.contentClaims
      ?.map((item) =>
        unwrapValue<AllergenClaimItem>(
          item as unknown as AllergenClaimItem | { value?: AllergenClaimItem | null } | null
        )
      )
      .filter((item): item is AllergenClaimItem => Boolean(item)) || null;

  const maxClaimsCount = toNonNegativeInt(
    productDetailSettings?.fields?.maxClaimsIndicatorCount?.value
  );

  const hideConsumerStatements =
    productDetailSettings?.fields?.hideProductConsumerStatements?.value ?? false;

  const verticallyAlignTitle = productDetailSettings?.fields?.verticallyAlignTitle?.value ?? false;

  // heroVariationInternational field not yet in generated model — access via type assertion
  const heroVariationInternational =
    (productDetailSettings?.fields as Record<string, { value?: boolean } | undefined>)
      ?.heroVariationInternational?.value ?? false;

  if (!product) {
    return <div>No Product found</div>;
  }

  const productHeroData = extractProductHeroData(
    product,
    productRouteDetails,
    breadcrumbData,
    siteSettings
  );

  return (
    <div>
      <ProductHero
        {...productHeroData}
        caseCount={product.ProductReferenceOnly?.caseCount ?? product.CaseCount}
        description={product.Description ?? undefined}
        heroActionToolbarIdentifier={heroActionToolbarIdentifier}
        heroActionToolbarSettings={heroActionToolbarSettings}
        pdpHeroSettings={{
          ...productHeroData.pdpHeroSettings,
          showCaseCount: productDetailSettings?.fields?.showCaseCount?.value,
          showPrimaryCta: productDetailSettings?.fields?.showPrimaryCta?.value,
          showProductCode: productDetailSettings?.fields?.showProductCode?.value,
          showSecondaryCta: productDetailSettings?.fields?.showSecondaryCta?.value,
          verticallyAlignTitle,
          heroVariationInternational,
        }}
        primaryCta={primaryCta}
        product={product}
        productCode={product?.ProductId ? parseInt(product.ProductId, 10).toString() : null}
        secondaryCta={secondaryCta}
      />
      {/* PDP Container */}
      <ProductContentContainer
        product={product}
        contentActionToolbarSettings={contentActionToolbarSettings}
        contentActionToolbarIdentifier={contentActionToolbarIdentifier}
        hideConsumerStatements={hideConsumerStatements}
        verticallyAlignTitle={verticallyAlignTitle}
        hideDescription={heroVariationInternational}
        {...(isGMFSSite ? { allergenClaims, maxClaimsCount } : {})}
      />
    </div>
  );
}
